import type { SelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { IconRef } from '@shared/ui/icons'

import { LearnWayReason } from '@brighterly/lib-core-types/subscription/enums'

import { useSubscriptionCancelStore } from '@entities/subscription'
import { useListsStore } from '@shared/model'
import { trackEvent } from '@shared/lib/amplitude'
import { toAnalyticsId } from '@shared/lib/amplitude'

import { useSubscriptionsApi } from '../../api/subscriptions.hook'
import { useSubscriptionCancelApi } from '../../api/subscription-cancel.hook'

interface LearnWayReasonsReturn {
  reasons: ComputedRef<SelectOption<LearnWayReason>[]>
  icons: Record<LearnWayReason, IconRef>
  setLearnWayReason: (value: LearnWayReason | null) => void
}

const LEARN_WAY_LABELS: Partial<Record<LearnWayReason, string>> = {
  [LearnWayReason.DecreasePace]: 'Change lesson pace',
}

export const useLearnWayReason = (): LearnWayReasonsReturn => {
  const subscriptionCancelStore = useSubscriptionCancelStore()
  const listsStore = useListsStore()

  const reasons = computed<SelectOption<LearnWayReason>[]>(() => {
    return Object.entries(listsStore.getLearnWaySubType!).map(item => {
      const value = item[0] as LearnWayReason
      const text = item[1] as string
      return {
        id: value,
        value,
        text: LEARN_WAY_LABELS[value] ?? text,
        selected: value === subscriptionCancelStore.getLearnWayReason,
        disabled: false,
      }
    })
  })

  const icons: Record<LearnWayReason, IconRef> = {
    [LearnWayReason.DecreasePace]: { name: 'calendar-lines-pen' },
    [LearnWayReason.ChangeSchedule]: { name: 'arrows-rotate' },
  }

  const setLearnWayReason = async (value: LearnWayReason | null): Promise<void> => {
    if (value !== subscriptionCancelStore.getLearnWayReason) {
      trackEvent('Field_change', {
        feature: 'subscription',
        name: 'btn_subscription_cancel_sub_reason',
        value: value ? toAnalyticsId(value) : null,
      })
      subscriptionCancelStore.setLearnWayReason(value)

      const isDecreasePace = (subscriptionCancelStore.getLearnWayReason === LearnWayReason.DecreasePace)
      const isChangeSchedule = (subscriptionCancelStore.getLearnWayReason === LearnWayReason.ChangeSchedule)

      if (isDecreasePace) {
        if (subscriptionCancelStore.isOneLessonPerWeek) {
          void useSubscriptionCancelApi({
            onSuccess: async () => {
              await useSubscriptionsApi()
              subscriptionCancelStore.setCloseGeneralModals()
              subscriptionCancelStore.setModalSuccess(true)
            },
          })
        } else {
          subscriptionCancelStore.setModalTryLearnWay(false)
          subscriptionCancelStore.setModalChangePace(true)
        }
      }

      if (isChangeSchedule) {
        await useSubscriptionCancelApi({
          onSuccess: async () => {
            await useSubscriptionsApi()
            subscriptionCancelStore.setCloseGeneralModals()
            subscriptionCancelStore.setModalSuccess(true)
          },
        })
      }
    }
  }

  return {
    reasons,
    icons,
    setLearnWayReason,
  }
}
